import React from 'react';
import { UseFormRegister, Control, FieldErrors, UseFormWatch, UseFormTrigger } from 'react-hook-form';
import { ContactFormValues } from './ContactForm';
import { Callout, Icon } from '../ui';
import LocationSelector from '../LocationSelector';
import PhoneInputField from './PhoneInputField';
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete';

interface ContactFormFieldsProps {
  register: UseFormRegister<ContactFormValues>;
  control: Control<ContactFormValues>;
  errors: FieldErrors<ContactFormValues>;
  touchedFields: Partial<Record<keyof ContactFormValues, boolean>>;
  watch: UseFormWatch<ContactFormValues>;
  trigger: UseFormTrigger<ContactFormValues>;
  isRTL?: boolean;
  isEditMode?: boolean;
  showTemporaryOption?: boolean;
  useTemporaryAddress?: boolean;
  onTemporaryToggle?: (value: boolean) => void;
  lockedCountryId?: number | null;
  lockedCountryName?: string;
  lockedCountryIso2?: string;
  onLocationChange: (locationData: any) => void;
  onGooglePlaceSelect: (locationData: any) => void;
}

export const ContactFormFields: React.FC<ContactFormFieldsProps> = ({
  register,
  control,
  errors,
  touchedFields,
  watch,
  trigger,
  isRTL,
  isEditMode,
  showTemporaryOption,
  useTemporaryAddress,
  onTemporaryToggle,
  lockedCountryId,
  lockedCountryName,
  lockedCountryIso2,
  onLocationChange,
  onGooglePlaceSelect,
}) => {
  const phoneCountry = lockedCountryIso2?.toLowerCase() || 'il';
  const phoneHelperText = lockedCountryIso2?.toUpperCase() === 'US'
    ? 'Include country code (e.g., +1 for the United States)'
    : 'Include country code (e.g., +972 for Israel)';

  return (
    <>
      {/* Temporary address toggle */}
      {showTemporaryOption && (
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
            <input
              type="checkbox"
              checked={useTemporaryAddress}
              onChange={(e) => onTemporaryToggle?.(e.target.checked)}
            />
            Use as temporary address (not saved to your account)
          </label>
          {useTemporaryAddress && (
            <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              This address will be available only for this session
            </p>
          )}
        </div>
      )}

      {/* ── Contact Information ── */}
      <div style={{ padding: 20, border: '1px solid var(--line)', borderRadius: 10, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Icon name="user" size={16} style={{ color: 'var(--brand-blue)' }} />
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--brand-blue)' }}>Contact Information</h3>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {/* Nickname */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
              Nickname / Label <span style={{ color: 'var(--err)' }}>*</span>
            </label>
            <input
              className={`input${errors.nickname ? ' input-error' : ''}`}
              placeholder="e.g. Home, Office, Warehouse"
              maxLength={50}
              {...register('nickname', { onChange: () => trigger('nickname') })}
            />
            {errors.nickname
              ? <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--err)' }}>{errors.nickname.message}</p>
              : <p className="muted" style={{ margin: '4px 0 0', fontSize: 11 }}>Quick reference name for this contact</p>}
          </div>

          {/* Full Name */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
              Full Name <span style={{ color: 'var(--err)' }}>*</span>
            </label>
            <input
              className={`input${errors.contactName ? ' input-error' : ''}`}
              placeholder="Enter full name"
              maxLength={100}
              {...register('contactName', { onChange: () => trigger('contactName') })}
            />
            {errors.contactName && <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--err)' }}>{errors.contactName.message}</p>}
          </div>

          {/* Company */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
              Company Name
            </label>
            <input
              className="input"
              placeholder="Company or organization (optional)"
              maxLength={100}
              {...register('companyName', { onChange: () => trigger('companyName') })}
            />
            {errors.companyName && <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--err)' }}>{errors.companyName.message}</p>}
          </div>

          {/* Email */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
              Email Address <span style={{ color: 'var(--err)' }}>*</span>
            </label>
            <input
              type="email"
              className={`input${errors.email ? ' input-error' : ''}`}
              placeholder="example@domain.com"
              maxLength={254}
              {...register('email', { onChange: () => trigger('email') })}
            />
            {errors.email && <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--err)' }}>{errors.email.message}</p>}
          </div>

          {/* Phone */}
          <div style={{ gridColumn: '1 / -1' }}>
            <PhoneInputField
              name="phone"
              control={control}
              label="Phone Number"
              placeholder="Enter phone number"
              required
              country={phoneCountry}
              preferredCountries={['il', 'us', 'gb', 'ca', 'au', 'de', 'fr', 'ae', 'sa', 'jo', 'eg']}
              enableAreaCodes={true}
              enableSearch={true}
              autoFormat={true}
              rules={{
                required: 'Phone number is required',
                minLength: { value: 8, message: 'Phone number must be at least 8 digits' },
                maxLength: { value: 20, message: 'Phone number is too long' },
                pattern: { value: /^[+]?[\d\s\-()]{8,20}$/, message: 'Please enter a valid phone number' },
              }}
              helperText={phoneHelperText}
              onPhoneChange={() => trigger('phone')}
            />
          </div>
        </div>
      </div>

      {/* ── Address Information ── */}
      <div style={{ padding: 20, border: '1px solid var(--line)', borderRadius: 10, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Icon name="mapPin" size={16} style={{ color: 'var(--brand-blue)' }} />
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--brand-blue)' }}>Address Information</h3>
        </div>

        {lockedCountryId && (
          <Callout tone="info" style={{ marginBottom: 14, fontSize: 13 }}>
            Country is locked to {lockedCountryName || lockedCountryIso2 || 'the required destination'} for internal shipments.
          </Callout>
        )}

        {/* Google Places */}
        <div style={{ marginBottom: 16 }}>
          <GooglePlacesAutocomplete
            onLocationSelect={onGooglePlaceSelect}
            placeholder="Search for your complete address..."
            label="Smart Address Search"
            helperText="Start typing your address for instant suggestions, or skip to fill manually below"
            isRTL={isRTL}
            countryRestriction={lockedCountryIso2}
          />
        </div>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0' }}>
          <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
          <span className="muted" style={{ padding: '0 12px', fontSize: 12 }}>OR FILL MANUALLY</span>
          <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
        </div>

        {/* Location selector */}
        <div style={{ marginBottom: 14 }}>
          <LocationSelector
            value={{
              country_id: watch('country_id') || undefined,
              state_id: watch('state_id') || undefined,
              city_id: watch('city_id') || undefined,
              city_other: watch('city_other') || '',
            }}
            onChange={(locationData) => {
              onLocationChange(locationData);
              setTimeout(() => trigger(['country_id', 'city_id', 'city_other']), 100);
            }}
            errors={{
              country_id: errors.country_id?.message || (touchedFields.country_id && !watch('country_id') ? 'Country is required' : undefined),
              city_id: errors.city_id?.message || (touchedFields.city_id && !watch('city_id') && !watch('city_other') ? 'City is required' : undefined),
              city_other: errors.city_other?.message,
            }}
            isRTL={isRTL}
            lockedCountryId={lockedCountryId}
            lockedCountryName={lockedCountryName}
          />
        </div>

        {/* Street / house / zip */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
              Street Address <span style={{ color: 'var(--err)' }}>*</span>
            </label>
            <input
              className={`input${errors.street ? ' input-error' : ''}`}
              placeholder="Enter street address"
              maxLength={200}
              {...register('street', { onChange: () => trigger('street') })}
            />
            {errors.street && <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--err)' }}>{errors.street.message}</p>}
          </div>
          <div style={{ minWidth: 110 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>House/Bldg</label>
            <input className="input" placeholder="Apt, Suite" maxLength={20} {...register('houseNo')} />
          </div>
          <div style={{ minWidth: 130 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
              Zip Code <span style={{ color: 'var(--err)' }}>*</span>
            </label>
            <input
              className={`input${errors.zipCode ? ' input-error' : ''}`}
              placeholder="12345"
              maxLength={20}
              {...register('zipCode', { onChange: () => trigger('zipCode') })}
            />
            {errors.zipCode && <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--err)' }}>{errors.zipCode.message}</p>}
          </div>
        </div>

        {/* Delivery instructions */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
            Delivery Instructions
          </label>
          <textarea
            className="input"
            rows={3}
            placeholder="Special delivery instructions, gate codes, landmarks, etc."
            maxLength={500}
            style={{ resize: 'vertical' }}
            {...register('deliveryDetails', { onChange: () => trigger('deliveryDetails') })}
          />
          {errors.deliveryDetails && <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--err)' }}>{errors.deliveryDetails.message}</p>}
        </div>
      </div>

      {/* ── Preferences (not in temporary mode) ── */}
      {!useTemporaryAddress && (
        <div style={{ padding: 20, border: '1px solid var(--line)', borderRadius: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Icon name="star" size={16} style={{ color: 'var(--brand-blue)' }} />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--brand-blue)' }}>Contact Preferences</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" style={{ marginTop: 2 }} {...register('isFavorite')} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Mark as favorite</div>
                <div className="muted" style={{ fontSize: 12 }}>Favorite contacts appear at the top of your list for quick access</div>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" style={{ marginTop: 2 }} {...register('isDefault')} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Set as default address</div>
                <div className="muted" style={{ fontSize: 12 }}>This address will be used as the default source address for shipping</div>
              </div>
            </label>
            {isEditMode && (
              <Callout tone="info" style={{ fontSize: 13 }}>
                <strong>Note:</strong> Setting this contact as default will automatically unset any previously set default address.
              </Callout>
            )}
          </div>
        </div>
      )}
    </>
  );
};
